// Step3DateTimeCourt — ActivityWizard Steg 3 (delspår multi-bana): datum + längd + bana-läge
// + tid. "Var + när". Moved here from step 2 in the step-reorder (typ → spelare → bana → tid)
// so the court count N is known from the player count (step 2) BEFORE the court/time choice.
//
// Multi-bana core: N = deriveRequiredCourts(spotsTotal). Auto → TimeSlotGrid shows only times
// where >= N of the auto pool are free SIMULTANEOUSLY. Specific → MultiCourtPicker picks
// EXACTLY N courts (2-player court excluded), and the grid shows times where all N are free.
// MultiCourtInfo shows the correct "X spelare = N banor" (player count now known). Court-before-
// time invalidation kept: changing N/mode/court clears a now-invalid time. Read-only (3g books).

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useActivityWizard } from "../ActivityWizardContext";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useDayCourtBookings } from "@/hooks/useDayCourtBookings";
import { deriveRequiredCourts, durationOptions, filterAutoPoolCourtIds, type CourtMode } from "@/lib/wizardSlots";
import DateSelector from "../DateSelector";
import DurationSelector from "../DurationSelector";
import WantsCourtToggle from "../WantsCourtToggle";
import CourtModeSelector, { type ActiveCourtMode } from "../CourtModeSelector";
import MultiCourtPicker, { type CourtLite } from "../MultiCourtPicker";
import MultiCourtInfo from "../MultiCourtInfo";
import TimeSlotGrid from "../TimeSlotGrid";
import WizardStepHero from "../WizardStepHero";

type HallRow = {
  opening_time: string;
  closing_time: string;
  time_step_minutes: number | null;
  min_duration_minutes: number | null;
  max_duration_minutes: number | null;
} | null;

type CourtRow = { id: string; name: string; max_players: number; status: string };

export default function Step3DateTimeCourt() {
  const { t } = useTranslation("admin");
  const { state, dispatch } = useActivityWizard();
  const { hall, courts, isLoading: hallLoading } = useOrgHall();

  const fd = state.formData;
  const date = fd.date ?? null;
  const durationMinutes = fd.durationMinutes ?? null;
  const startTime = fd.startTime ?? null;
  const wantsCourt = fd.wantsCourt ?? true;
  const courtMode: ActiveCourtMode = (fd.courtMode as ActiveCourtMode) ?? "auto";
  const selectedCourtIds = useMemo(() => fd.courtIds ?? [], [fd.courtIds]);
  const spotsTotal = fd.spotsTotal ?? 4;
  const requiredCourts = deriveRequiredCourts(spotsTotal);

  const hallRow = hall as HallRow;
  // Auto pool = active courts with max_players >= 4 (2-player court excluded). Both the
  // picker list and the auto availability use this pool (beslut #4/#6).
  const autoPoolCourts: CourtLite[] = useMemo(
    () =>
      filterAutoPoolCourtIds(courts as CourtRow[])
        .map((id) => (courts as CourtRow[]).find((c) => c.id === id))
        .filter((c): c is CourtRow => !!c)
        .map((c) => ({ id: c.id, name: c.name })),
    [courts],
  );
  const autoPoolCourtIds = useMemo(() => autoPoolCourts.map((c) => c.id), [autoPoolCourts]);

  const durOpts = useMemo(() => {
    if (!hallRow) return [];
    return durationOptions(
      hallRow.min_duration_minutes ?? 30,
      hallRow.max_duration_minutes ?? 120,
      hallRow.time_step_minutes ?? 30,
    );
  }, [hallRow]);

  const { bookings, isLoading: bookingsLoading } = useDayCourtBookings(date, autoPoolCourtIds);

  const update = (payload: Partial<typeof fd>) => dispatch({ type: "UPDATE_FORM", payload });
  const handleDate = (iso: string) => update({ date: iso, startTime: null, endTime: null });
  const handleDuration = (min: number) => update({ durationMinutes: min, startTime: null, endTime: null });
  const handleSlot = (start: string, end: string) => update({ startTime: start, endTime: end });
  const clearTime = useCallback(
    () => dispatch({ type: "UPDATE_FORM", payload: { startTime: null, endTime: null } }),
    [dispatch],
  );
  // Toggle a court in the specific-mode selection, capped at N (picker also disables beyond N).
  const toggleCourt = (id: string) => {
    if (selectedCourtIds.includes(id)) {
      update({ courtIds: selectedCourtIds.filter((c) => c !== id) });
    } else if (selectedCourtIds.length < requiredCourts) {
      update({ courtIds: [...selectedCourtIds, id] });
    }
  };

  // 'none' = no court reserved (all open-hours slots).
  const slotMode: CourtMode = wantsCourt ? courtMode : "none";
  // Specific needs exactly N courts before the grid is meaningful — hide + hint until then.
  const needsCourtPick = wantsCourt && courtMode === "specific" && selectedCourtIds.length < requiredCourts;
  const showGrid = Boolean(date && durationMinutes) && !needsCourtPick;

  const hallTimes = hallRow
    ? {
        opening_time: hallRow.opening_time,
        closing_time: hallRow.closing_time,
        time_step_minutes: hallRow.time_step_minutes ?? 30,
      }
    : null;

  const heading = "mb-2 text-sm font-medium text-muted-foreground";

  return (
    <div className="space-y-4 md:space-y-6">
      <WizardStepHero
        title={t("activityWizard.step2.heroTitle", { defaultValue: "När och var?" })}
        subtitle={t("activityWizard.step2.heroSubtitle", {
          defaultValue: "Välj dag, längd och tid. Vi hittar lediga banor åt dig.",
        })}
      />
      <section>
        <h2 className={heading}>{t("activityWizard.step2.dateHeading", { defaultValue: "Välj datum" })}</h2>
        <DateSelector value={date} onSelect={handleDate} />
      </section>

      <section>
        <h2 className={heading}>{t("activityWizard.step2.durationHeading", { defaultValue: "Längd" })}</h2>
        <DurationSelector value={durationMinutes} options={durOpts} onSelect={handleDuration} />
      </section>

      <section>
        <h2 className={heading}>{t("activityWizard.step3.wantsCourtHeading", { defaultValue: "Vill du boka bana?" })}</h2>
        <WantsCourtToggle value={wantsCourt} onChange={(v) => update({ wantsCourt: v })} />
      </section>

      {wantsCourt && (
        <section className="space-y-2">
          <CourtModeSelector
            mode={courtMode}
            onModeChange={(m) => update({ courtMode: m, courtIds: [] })}
          />
          {courtMode === "specific" && (
            <MultiCourtPicker
              courts={autoPoolCourts}
              selectedCourtIds={selectedCourtIds}
              requiredCourts={requiredCourts}
              onToggle={toggleCourt}
            />
          )}
          <MultiCourtInfo players={spotsTotal} requiredCourts={requiredCourts} autoPoolSize={autoPoolCourtIds.length} />
        </section>
      )}

      {date && durationMinutes ? (
        <section>
          <h2 className={heading}>{t("activityWizard.step2.timeHeading", { defaultValue: "Välj tid" })}</h2>
          {showGrid ? (
            <TimeSlotGrid
              date={date}
              durationMinutes={durationMinutes}
              mode={slotMode}
              autoPoolCourtIds={autoPoolCourtIds}
              selectedCourtIds={selectedCourtIds}
              requiredCourts={requiredCourts}
              hall={hallTimes}
              bookings={bookings}
              isLoading={bookingsLoading || hallLoading}
              startTime={startTime}
              onSelect={handleSlot}
              onInvalidateTime={clearTime}
            />
          ) : (
            <p
              data-testid="pick-court-first"
              className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground"
            >
              {t("activityWizard.step3.multiCourtPicker.heading", {
                n: requiredCourts,
                defaultValue: `Välj ${requiredCourts} banor`,
              })}
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
